"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoomMonitor = void 0;
const rxjs_1 = require("rxjs");
const media_1 = require("../util/media");
const logger_1 = require("../util/logger");
class RoomMonitor {
    get roomMonitorStatsBuffer() {
        return this._roomMonitorStatsBuffer;
    }
    constructor(room) {
        this.room = room;
        this.ROOM_STATS_BUFFER_LENGTH = 20;
        this.ROOM_STATS_POLLING_INTERVAL = 1000;
        this.LOW_FRAME_RATE_THRESHOLD = 2;
        this.destroyed = new rxjs_1.Subject();
        // The shiny, polished calcuated room stats
        this.roomMonitorStatsSubject = new rxjs_1.Subject();
        this.roomMonitorStats$ = this.roomMonitorStatsSubject.asObservable();
        // We want a performance history over time available to send with error/performance logging when needed
        // Maintaining a rolling buffer lets us track a meaningful length of time without risking a memory leak by tracking EVERYTHING
        this._roomMonitorStatsBuffer = [];
        this.roomWebRtcStatsSubject = new rxjs_1.Subject();
        this.roomWebRtcStats$ = this.roomWebRtcStatsSubject.asObservable();
        this.isLocalVideoFrozenSubject = new rxjs_1.Subject();
        this.isLocalVideoFrozen$ = this.isLocalVideoFrozenSubject.asObservable();
        // This is the RAW previous room stat report
        // It is needed to calculate values against the current stats before we're able to construct the room monitor stats
        this.previousRawRoomStats = null;
        this.currentRawRoomStats = null;
        this.pollingInterval$ = (0, rxjs_1.interval)(this.ROOM_STATS_POLLING_INTERVAL);
        this.pollingInterval$
            .pipe((0, rxjs_1.takeUntil)(this.destroyed))
            .subscribe(() => this.updateRoomStats());
    }
    disconnect() {
        this.destroyed.next(undefined);
        logger_1.logger.info('Room monitor disconnected');
    }
    updateRoomStats() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
        return __awaiter(this, void 0, void 0, function* () {
            // Cannot poll stats on a room that is not yet connected
            if (typeof ((_a = this.room) === null || _a === void 0 ? void 0 : _a.getStats) !== 'function' || ((_b = this.room) === null || _b === void 0 ? void 0 : _b.state) !== 'connected') {
                return;
            }
            try {
                const [rawRoomStats] = yield this.room.getStats();
                if (!rawRoomStats) {
                    return;
                }
                this.previousRawRoomStats = Object.assign({}, this.currentRawRoomStats);
                this.currentRawRoomStats = rawRoomStats;
                // Local Audio
                const recentLocalAudioStats = [];
                if (((_d = (_c = this.previousRawRoomStats) === null || _c === void 0 ? void 0 : _c.localAudioTrackStats) === null || _d === void 0 ? void 0 : _d.length) > 0) {
                    recentLocalAudioStats.push(this.previousRawRoomStats.localAudioTrackStats[0]);
                }
                if (((_f = (_e = this.currentRawRoomStats) === null || _e === void 0 ? void 0 : _e.localAudioTrackStats) === null || _f === void 0 ? void 0 : _f.length) > 0) {
                    recentLocalAudioStats.push(this.currentRawRoomStats.localAudioTrackStats[0]);
                }
                let localAudioTrackStats = null;
                if (recentLocalAudioStats.length === 2) {
                    const [prevStats, currStats] = recentLocalAudioStats;
                    localAudioTrackStats = this.buildLocalAudioTrackStats(prevStats, currStats);
                }
                // Local Video
                const recentLocalVideoStats = [];
                if (((_h = (_g = this.previousRawRoomStats) === null || _g === void 0 ? void 0 : _g.localVideoTrackStats) === null || _h === void 0 ? void 0 : _h.length) > 0) {
                    recentLocalVideoStats.push(this.previousRawRoomStats.localVideoTrackStats[0]);
                }
                if (((_j = this.currentRawRoomStats.localVideoTrackStats) === null || _j === void 0 ? void 0 : _j.length) > 0) {
                    recentLocalVideoStats.push(this.currentRawRoomStats.localVideoTrackStats[0]);
                }
                let localVideoTrackStats = null;
                if (recentLocalVideoStats.length === 2) {
                    const [prevStats, currStats] = recentLocalVideoStats;
                    localVideoTrackStats = this.buildLocalVideoTrackStats(prevStats, currStats);
                }
                // Remote Audio
                const recentRemoteAudioStats = [];
                if (((_l = (_k = this.previousRawRoomStats) === null || _k === void 0 ? void 0 : _k.remoteAudioTrackStats) === null || _l === void 0 ? void 0 : _l.length) > 0) {
                    recentRemoteAudioStats.push(this.previousRawRoomStats.remoteAudioTrackStats[0]);
                }
                if (((_m = this.currentRawRoomStats.remoteAudioTrackStats) === null || _m === void 0 ? void 0 : _m.length) > 0) {
                    recentRemoteAudioStats.push(this.currentRawRoomStats.remoteAudioTrackStats[0]);
                }
                let remoteAudioTrackStats;
                if (recentRemoteAudioStats.length === 2) {
                    const [prevStats, currStats] = recentRemoteAudioStats;
                    remoteAudioTrackStats = this.buildRemoteAudioTrackStats(prevStats, currStats);
                }
                // Remote Video
                const recentRemoteVideoStats = [];
                if (((_p = (_o = this.previousRawRoomStats) === null || _o === void 0 ? void 0 : _o.remoteVideoTrackStats) === null || _p === void 0 ? void 0 : _p.length) > 0) {
                    recentRemoteVideoStats.push(this.previousRawRoomStats.remoteVideoTrackStats[0]);
                }
                if (((_q = this.currentRawRoomStats.remoteVideoTrackStats) === null || _q === void 0 ? void 0 : _q.length) > 0) {
                    recentRemoteVideoStats.push(this.currentRawRoomStats.remoteVideoTrackStats[0]);
                }
                let remoteVideoTrackStats = null;
                if (recentRemoteVideoStats.length === 2) {
                    const [prevStats, currStats] = recentRemoteVideoStats;
                    remoteVideoTrackStats = this.buildRemoteVideoTrackStats(prevStats, currStats);
                }
                const roomMonitorStats = {
                    localAudioTrackStats,
                    localVideoTrackStats,
                    remoteAudioTrackStats,
                    remoteVideoTrackStats,
                };
                // Check to see if the local video frame rate is above our set threshold, if not we will restart the video track
                let isPreviousLocalVideoFrozen = false;
                if (Object.entries(this.previousRawRoomStats).length > 0) {
                    isPreviousLocalVideoFrozen =
                        ((_s = (_r = this.previousRawRoomStats.localVideoTrackStats[0]) === null || _r === void 0 ? void 0 : _r.bytesSent) !== null && _s !== void 0 ? _s : 0) > 0 &&
                            ((_u = (_t = this.previousRawRoomStats.localVideoTrackStats[0]) === null || _t === void 0 ? void 0 : _t.frameRate) !== null && _u !== void 0 ? _u : 0) <
                                this.LOW_FRAME_RATE_THRESHOLD;
                }
                const isCurrentLocalVideoFrozen = ((_w = (_v = rawRoomStats.localVideoTrackStats[0]) === null || _v === void 0 ? void 0 : _v.bytesSent) !== null && _w !== void 0 ? _w : 0) > 0 &&
                    ((_y = (_x = rawRoomStats.localVideoTrackStats[0]) === null || _x === void 0 ? void 0 : _x.frameRate) !== null && _y !== void 0 ? _y : 0) <
                        this.LOW_FRAME_RATE_THRESHOLD;
                this.roomMonitorStatsSubject.next(roomMonitorStats);
                this.roomWebRtcStatsSubject.next(rawRoomStats);
                if (isPreviousLocalVideoFrozen !== isCurrentLocalVideoFrozen)
                    this.isLocalVideoFrozenSubject.next(isCurrentLocalVideoFrozen);
                // Add to the buffer first before updating variables
                this._roomMonitorStatsBuffer.push(roomMonitorStats);
                // Only keep last 20 reconds/seconds of room stats
                this._roomMonitorStatsBuffer = this._roomMonitorStatsBuffer.slice(this.ROOM_STATS_BUFFER_LENGTH * -1);
            }
            catch (e) {
                logger_1.logger.error('Room monitor could not get room stats: ', e);
            }
        });
    }
    buildLocalAudioTrackStats(prevStats, currStats) {
        const { bytesSent: prevByteTotal, timestamp: prevTimestamp } = prevStats;
        const { bytesSent: currentByteTotal, timestamp: currentTimestamp, jitter, roundTripTime, audioLevel, packetsLost, packetsSent, codec, } = currStats;
        const bitRate = (0, media_1.calculateTrackBitRate)(prevByteTotal, prevTimestamp, currentByteTotal, currentTimestamp);
        const localAudioTrackStats = {
            codec,
            bitRate: bitRate,
            packetsLost,
            packetsSent,
            jitter,
            audioLevel,
            roundTripTime,
        };
        return localAudioTrackStats;
    }
    buildLocalVideoTrackStats(prevStats, currStats) {
        const { bytesSent: prevByteTotal, timestamp: prevTimestamp } = prevStats;
        const { bytesSent: currentByteTotal, timestamp: currentTimestamp, jitter, roundTripTime, dimensions, frameRate, packetsLost, packetsSent, codec, } = currStats;
        const bitRate = (0, media_1.calculateTrackBitRate)(prevByteTotal, prevTimestamp, currentByteTotal, currentTimestamp);
        const localVideoTrackStats = {
            codec,
            bitRate: bitRate,
            packetsLost,
            dimensions,
            packetsSent,
            jitter,
            frameRate,
            roundTripTime,
        };
        return localVideoTrackStats;
    }
    buildRemoteVideoTrackStats(prevStats, currStats) {
        const { bytesReceived: prevByteTotal, timestamp: prevTimestamp } = prevStats;
        const { bytesReceived: currentByteTotal, timestamp: currentTimestamp, frameRate, packetsLost, dimensions, packetsReceived, codec, } = currStats;
        const bitRate = (0, media_1.calculateTrackBitRate)(prevByteTotal, prevTimestamp, currentByteTotal, currentTimestamp);
        const remoteVideoTrackStats = {
            codec,
            bitRate: bitRate,
            packetsLost,
            dimensions,
            packetsReceived,
            frameRate,
        };
        return remoteVideoTrackStats;
    }
    buildRemoteAudioTrackStats(prevStats, currStats) {
        const { bytesReceived: prevByteTotal, timestamp: prevTimestamp } = prevStats;
        const { bytesReceived: currentByteTotal, timestamp: currentTimestamp, jitter, audioLevel, packetsLost, packetsReceived, codec, } = currStats;
        const bitRate = (0, media_1.calculateTrackBitRate)(prevByteTotal, prevTimestamp, currentByteTotal, currentTimestamp);
        const remoteAudioTrackStats = {
            codec,
            bitRate: bitRate,
            packetsLost,
            packetsReceived,
            jitter,
            audioLevel,
        };
        return remoteAudioTrackStats;
    }
}
exports.RoomMonitor = RoomMonitor;
//# sourceMappingURL=room_monitor.js.map